import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BookmarkCheck, BookmarkPlus, Eye, EyeOff } from 'lucide-react';
import { Button } from 'components/ui/button';
import Skeleton from 'components/ui/skeleton';
import { SegmentedTabs } from 'components/ui/data-table';
import { cn } from 'lib/utils';
import { CEFR_LEVELS, WORD_TOPICS, type CefrLevel, type ListWord, type WordTopic } from './types';
import { useWordList } from './useWordList';

interface Props {
  /** Lower-cased words already in "So'zlarim". */
  savedWords: Set<string>;
  onSave: (word: ListWord) => Promise<void>;
}

const LEVEL_TONE: Record<CefrLevel, string> = {
  B1: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300',
  B2: 'bg-sky-50 text-sky-700 dark:bg-sky-950/40 dark:text-sky-300',
  C1: 'bg-violet-50 text-violet-700 dark:bg-violet-950/40 dark:text-violet-300',
};

/** IELTS topic words by CEFR level with Uzbek and Russian translations; any word can be saved to "So'zlarim". */
export function WordLists({ savedWords, onSave }: Props) {
  const { t, i18n } = useTranslation();
  const { words, failed } = useWordList();
  const [level, setLevel] = useState<CefrLevel>('B2');
  const [topic, setTopic] = useState<WordTopic | 'all'>('all');
  // Self-test: hide the translations and recall them first.
  const [hidden, setHidden] = useState(false);
  const [revealed, setRevealed] = useState<Set<string>>(new Set());
  const [saving, setSaving] = useState<string | null>(null);
  const showRussian = i18n.language.startsWith('ru');

  const shown = useMemo(
    () => (words ?? []).filter((w) => w.level === level && (topic === 'all' || w.topic === topic)),
    [words, level, topic],
  );

  if (failed) return <p className="p-5 text-sm text-destructive">{t('wordLists.load_failed')}</p>;
  if (!words) {
    return (
      <div className="space-y-3 p-5" aria-hidden="true">
        <Skeleton className="h-4 w-1/3" />
        <Skeleton className="h-4 w-2/3" />
      </div>
    );
  }

  const save = async (word: ListWord) => {
    setSaving(word.word);
    try {
      await onSave(word);
    } finally {
      setSaving(null);
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <SegmentedTabs<CefrLevel>
          aria-label={t('wordLists.level')}
          className="h-9"
          options={CEFR_LEVELS.map((value) => ({ value, label: value }))}
          value={level}
          onChange={setLevel}
        />
        <Button type="button" variant="outline" size="sm" className="gap-1.5" aria-pressed={hidden}
          onClick={() => { setHidden((h) => !h); setRevealed(new Set()); }}>
          {hidden ? <Eye className="h-4 w-4" aria-hidden /> : <EyeOff className="h-4 w-4" aria-hidden />}
          {hidden ? t('wordLists.show_translations') : t('wordLists.hide_translations')}
        </Button>
      </div>
      <div role="group" aria-label={t('wordLists.topic')} className="flex flex-wrap gap-1.5">
        {(['all', ...WORD_TOPICS] as const).map((value) => (
          <button
            key={value}
            type="button"
            aria-pressed={topic === value}
            onClick={() => setTopic(value)}
            className={cn(
              'h-8 rounded-full border px-3 text-[13px] font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
              topic === value ? 'border-foreground bg-foreground text-background' : 'border-border text-muted-foreground hover:text-foreground',
            )}
          >
            {t(`wordLists.topic_${value}`)}
          </button>
        ))}
      </div>

      <ul className="divide-y divide-border overflow-hidden rounded-lg border border-border bg-card">
        {shown.map((item) => {
          const isSaved = savedWords.has(item.word.toLowerCase());
          const translation = showRussian ? item.ru : item.uz;
          const isHidden = hidden && !revealed.has(item.word);
          return (
            <li key={item.word} className="flex gap-3 px-4 py-3.5 md:px-5">
              <div className="min-w-0 flex-1">
                <p className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
                  <span className="font-display text-[17px] font-semibold text-foreground">{item.word}</span>
                  <span className="text-xs italic text-muted-foreground">{item.partOfSpeech}</span>
                  <span className={cn('rounded px-1.5 py-px text-[11px] font-semibold', LEVEL_TONE[item.level])}>{item.level}</span>
                </p>
                {isHidden ? (
                  <button
                    type="button"
                    onClick={() => setRevealed((prev) => new Set(prev).add(item.word))}
                    className="mt-1 text-sm font-medium text-muted-foreground underline decoration-dotted underline-offset-4 hover:text-foreground"
                  >
                    {t('wordLists.reveal')}
                  </button>
                ) : (
                  <p className="mt-1 text-sm font-medium text-sky-700 dark:text-sky-300">{translation}</p>
                )}
                <p className="mt-1 text-sm italic text-muted-foreground">“{item.example}”</p>
              </div>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                disabled={isSaved || saving === item.word}
                onClick={() => void save(item)}
                aria-label={isSaved ? t('wordLists.saved', { word: item.word }) : t('wordLists.save', { word: item.word })}
              >
                {isSaved ? <BookmarkCheck className="h-4 w-4 text-success-600" aria-hidden /> : <BookmarkPlus className="h-4 w-4" aria-hidden />}
              </Button>
            </li>
          );
        })}
      </ul>
      <p className="text-xs text-muted-foreground">{t('wordLists.count', { count: shown.length })}</p>
    </div>
  );
}
